import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Share } from "lucide-react";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);
const DISMISS_KEY = "ft_install_dismissed";

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;
    const onBIP = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    const onInstalled = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    if (isIOS() && !isStandalone()) setShow(true); // iOS has no beforeinstallprompt
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setShow(false);
      setDeferred(null);
    } else if (isIOS()) {
      setIosHint(true);
    }
  };

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, "1"); setShow(false); };

  if (!show) return null;

  return (
    <div
      className="lg:hidden fixed bottom-4 inset-x-4 z-[60] rounded-2xl border border-emerald-500/30 bg-[#111827]/95 backdrop-blur-xl p-4 shadow-[0_12px_40px_rgba(0,0,0,0.6)] animate-fadeUp"
      data-testid="install-prompt"
    >
      <div className="flex items-start gap-3">
        <img src="/icon-192.png" alt="Fuel Tracer" className="w-11 h-11 rounded-xl" />
        <div className="flex-1 min-w-0">
          <div className="font-[Outfit] font-semibold text-white">Install Fuel Tracer</div>
          <div className="text-xs text-[#9CA3AF] mt-0.5">Add it to your home screen for one-tap access — works like a real app.</div>
          {iosHint && (
            <div className="text-xs text-emerald-400 mt-2 flex items-center gap-1.5 flex-wrap">
              Tap the Share icon <Share className="w-3.5 h-3.5" /> then choose <b className="text-white">Add to Home Screen</b>
            </div>
          )}
        </div>
        <button onClick={dismiss} data-testid="install-dismiss" className="text-[#9CA3AF] hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <Button
        onClick={iosHint ? dismiss : install}
        data-testid="install-button"
        className="w-full mt-3 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
      >
        {iosHint ? "Got it" : <><Download className="w-4 h-4 mr-2" /> Install Fuel Tracer</>}
      </Button>
    </div>
  );
}
